import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowUpTrayIcon,
  DocumentMagnifyingGlassIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import authService from '../services/auth.service';
import dashboardService from '../services/dashboard.service';
import { User } from '../types/auth.types';
import { DashboardSummary, DashboardTrends, DashboardAlerts } from '../types/dashboard.types';
import AuthenticatedLayout from '../components/AuthenticatedLayout';
import {
  DashboardHeroSection,
  DashboardAlertsList,
  DashboardTrendChart,
  HeroSkeleton,
  AlertsSkeleton,
  ChartSkeleton,
} from '../components/dashboard';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');

  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  // Dashboard data state
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [trends, setTrends] = useState<DashboardTrends | null>(null);
  const [alerts, setAlerts] = useState<DashboardAlerts | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Period selector state (for trends chart)
  const [trendPeriod, setTrendPeriod] = useState(30);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (err) {
        navigate('/signin');
      } finally {
        setIsAuthLoading(false);
      }
    };
    fetchUserData();
  }, [navigate]);

  // Fetch dashboard data after auth is confirmed
  const fetchDashboardData = useCallback(async () => {
    if (!user) return;

    setIsDataLoading(true);
    setError(null);

    try {
      // Fetch all dashboard data in parallel
      const [summaryData, trendsData, alertsData] = await Promise.all([
        dashboardService.getSummary({ period_days: 30 }),
        dashboardService.getTrends({ period_days: trendPeriod }),
        dashboardService.getAlerts({ period_days: 7 }),
      ]);

      setSummary(summaryData);
      setTrends(trendsData);
      setAlerts(alertsData);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(t('dashboard:error.fetchFailed'));
    } finally {
      setIsDataLoading(false);
    }
  }, [user, trendPeriod, t]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user, fetchDashboardData]);

  // Handle period change for trends chart
  const handlePeriodChange = useCallback(async (days: number) => {
    setTrendPeriod(days);
    try {
      const trendsData = await dashboardService.getTrends({ period_days: days });
      setTrends(trendsData);
    } catch (err) {
      console.error('Failed to fetch trends:', err);
    }
  }, []);

  // Loading state
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <span className="sr-only">{t('common:app.loading')}</span>
      </div>
    );
  }

  return (
    <AuthenticatedLayout>
      <header>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
            {t('dashboard:title')}
          </h1>
        </div>
      </header>
      <main>
        <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="px-4 py-8 sm:px-0">
            {/* Error state */}
            {error && (
              <div className="mb-6 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            {/* Hero Section */}
            <section className="mb-8">
              <h2 className="sr-only">{t('dashboard:hero.sectionTitle')}</h2>
              {isDataLoading || !summary ? (
                <HeroSkeleton />
              ) : (
                <DashboardHeroSection summary={summary} />
              )}
            </section>

            {/* Trends and Alerts Section */}
            <section className="mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Trends Chart - 2/3 width on large screens */}
                <div className="lg:col-span-2">
                  {isDataLoading || !trends ? (
                    <ChartSkeleton />
                  ) : (
                    <DashboardTrendChart
                      trends={trends}
                      periodDays={trendPeriod}
                      onPeriodChange={handlePeriodChange}
                    />
                  )}
                </div>

                {/* Alerts List - 1/3 width on large screens */}
                <div>
                  {isDataLoading || !alerts ? (
                    <AlertsSkeleton />
                  ) : (
                    <DashboardAlertsList alerts={alerts} />
                  )}
                </div>
              </div>
            </section>

            {/* Quick Actions Section */}
            <section>
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                {t('dashboard:quickActions.title')}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {/* Upload Calls */}
                <Link
                  to="/uploads"
                  className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-indigo-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 transition-colors"
                >
                  <div className="flex-shrink-0">
                    <ArrowUpTrayIcon className="h-10 w-10 text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="absolute inset-0" aria-hidden="true" />
                    <p className="text-sm font-medium text-gray-900">
                      {t('dashboard:quickActions.upload.title')}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {t('dashboard:quickActions.upload.description')}
                    </p>
                  </div>
                </Link>

                {/* View Analyses */}
                <Link
                  to="/analyses"
                  className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-indigo-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 transition-colors"
                >
                  <div className="flex-shrink-0">
                    <DocumentMagnifyingGlassIcon className="h-10 w-10 text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="absolute inset-0" aria-hidden="true" />
                    <p className="text-sm font-medium text-gray-900">
                      {t('dashboard:quickActions.analyses.title')}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {t('dashboard:quickActions.analyses.description')}
                    </p>
                  </div>
                </Link>

                {/* Billing */}
                <Link
                  to="/billing"
                  className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-indigo-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 transition-colors"
                >
                  <div className="flex-shrink-0">
                    <CreditCardIcon className="h-10 w-10 text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="absolute inset-0" aria-hidden="true" />
                    <p className="text-sm font-medium text-gray-900">
                      {t('dashboard:quickActions.billing.title')}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {t('dashboard:quickActions.billing.description')}
                    </p>
                  </div>
                </Link>
              </div>
            </section>
          </div>
        </div>
      </main>
    </AuthenticatedLayout>
  );
};

export default Dashboard;
